"use client";

import { useState, useEffect, useRef } from "react";

interface HowItWorksVideoProps {
  video: string;
  image: string;
  title: string;
}

export function HowItWorksVideo({ video, image, title }: HowItWorksVideoProps) {
  const [isClient, setIsClient] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Ensure component only runs on client side to prevent hydration mismatch
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Handle video loading and autoplay
  useEffect(() => {
    if (isClient && videoRef.current) {
      const video = videoRef.current;

      // Preload the video
      video.preload = "auto";

      // Handle video load event
      const handleLoadedData = () => {
        setIsVideoLoaded(true);
        // Start playing once loaded
        video.play().catch(() => {
          // Autoplay failed, which is normal in some browsers
        });
      };

      video.addEventListener("loadeddata", handleLoadedData);

      // Start loading immediately
      video.load();

      return () => {
        video.removeEventListener("loadeddata", handleLoadedData);
      };
    }
  }, [isClient]);

  return (
    <div className="relative w-full h-full">
      {isClient ? (
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          poster={image}
          muted
          loop
          playsInline
          preload="auto"
          controls={false}
          disablePictureInPicture
          disableRemotePlayback
          style={{ opacity: isVideoLoaded ? 1 : 0.8 }}
        >
          <source src={video} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      ) : (
        <div
          className="w-full h-full object-cover bg-muted/20 flex items-center justify-center bg-cover bg-center"
          style={{
            backgroundImage: `url(${image})`,
          }}
        >
          <div className="text-muted-foreground text-sm">Video: {title}</div>
        </div>
      )}

      {/* Loading indicator */}
      {isClient && !isVideoLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  );
}
